import { useEffect, useMemo, useState } from 'react'
import { fetchExplorerWalletBalance } from '@/lib/explorerHolders'
import { isEthAddress, isOnChainCurveEnabled } from '@/lib/launchpad/contracts'
import { getSimHoldingForLaunch } from '@/lib/launchpad/store'
import type { LaunchState } from '@/lib/launchpad/types'
import { pledgedByWallet } from '@/lib/presale/math'
import type { SimPresale } from '@/lib/presale/types'
import { tokenSharePath } from '@/lib/shareSlug'
import { weiToEthString } from '@/lib/launchpad/wei'

export type ColonyHolding = {
  projectId: string
  amountLabel: string
  name: string
  ticker: string
  href: string
  note: string
}

const LOOKUP_BATCH = 4

function uniqueTokens(values: Array<string | undefined>) {
  const next = new Set<string>()
  for (const value of values) {
    if (!isEthAddress(value))
      continue
    next.add(value.toLowerCase())
  }
  return [...next]
}

async function mapInBatches<T, R>(items: T[], size: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = []
  for (let index = 0; index < items.length; index += size) {
    const batch = items.slice(index, index + size)
    out.push(...await Promise.all(batch.map(mapper)))
  }
  return out
}

export function useColonyHoldings(input: {
  address?: string
  launches: LaunchState[]
  presales: SimPresale[]
}) {
  const { address, launches, presales } = input
  const [onChainAmounts, setOnChainAmounts] = useState<Record<string, bigint>>({})
  const [loading, setLoading] = useState(false)

  const tokenKeys = useMemo(() => {
    const launchTokens = launches.map(launch => launch.tokenAddress || launch.contractAddress)
    const presaleTokens = presales.map(presale => presale.tokenAddress)
    return uniqueTokens([...launchTokens, ...presaleTokens]).join(',')
  }, [launches, presales])

  useEffect(() => {
    if (!address || !isOnChainCurveEnabled() || !tokenKeys) {
      setOnChainAmounts({})
      setLoading(false)
      return
    }

    let active = true
    const tokens = tokenKeys.split(',')
    setLoading(true)

    mapInBatches(tokens, LOOKUP_BATCH, async (token) => {
      const result = await fetchExplorerWalletBalance(token, address)
      return [token, result?.tokenAmount ?? 0n] as const
    }).then((rows) => {
      if (!active)
        return
      const next: Record<string, bigint> = {}
      for (const [token, amount] of rows) {
        if (amount > 0n)
          next[token] = amount
      }
      setOnChainAmounts(next)
    }).catch(() => {
      if (active)
        setOnChainAmounts({})
    }).finally(() => {
      if (active)
        setLoading(false)
    })

    return () => {
      active = false
    }
  }, [address, tokenKeys])

  const holdings = useMemo(() => {
    const rows = new Map<string, ColonyHolding>()

    const addRow = (row: ColonyHolding) => {
      if (!rows.has(row.projectId))
        rows.set(row.projectId, row)
    }

    for (const launch of launches) {
      const token = (launch.tokenAddress || launch.contractAddress || '').toLowerCase()
      const onChain = token ? onChainAmounts[token] ?? 0n : 0n
      const local = address ? getSimHoldingForLaunch(launch, address) : 0n
      const amount = onChain > local ? onChain : local
      if (amount <= 0n)
        continue
      addRow({
        projectId: launch.id,
        amountLabel: `${weiToEthString(amount, 4)} ${launch.ticker}`,
        name: launch.name,
        ticker: launch.ticker,
        href: tokenSharePath(launch.shareSlug, launch.id),
        note: launch.status === 'graduated' ? 'Graduated' : 'On curve',
      })
    }

    for (const presale of presales) {
      const token = (presale.tokenAddress || '').toLowerCase()
      const tokenAmount = token ? onChainAmounts[token] ?? 0n : 0n
      const pledgedWei = pledgedByWallet(presale, address)
      if (tokenAmount <= 0n && pledgedWei <= 0n)
        continue
      addRow({
        projectId: presale.id,
        amountLabel: tokenAmount > 0n
          ? `${weiToEthString(tokenAmount, 4)} ${presale.ticker}`
          : `${weiToEthString(pledgedWei, 4)} ETH pledged`,
        name: presale.name,
        ticker: presale.ticker,
        href: tokenSharePath(presale.shareSlug, presale.id),
        note: tokenAmount > 0n ? 'Presale tokens' : 'Presale pledge',
      })
    }

    return [...rows.values()]
  }, [address, launches, onChainAmounts, presales])

  return { holdings, loading }
}
